import {
  CategorySections,
  Module,
  Zone,
  Category,
  StoreType,
} from "@apis/index";
import ModuleDropdown from "@commons/modules/dropdown";
import Button from "@components/button";
import Column from "@components/column";
import Fields from "@components/fields";
import Row from "@components/row";
import Wrapper from "@components/wrapper";
import ButtonWrapper from "@components/wrapper/button-wrapper";
import { RootState } from "@store/index";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FormSection } from "../category/form/styles";

const SectionTitleForm = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { data: zones = [] } = useSelector((state: RootState) => state.zones);
  const { items: categories = [] } = useSelector(
    (state: RootState) => state.categories,
  );
  const {
    data: storeTypes,
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state.storeTypes);

  const [initialValues, setInitialValues] = useState<any>({
    moduleId: "",
    zoneIds: [] as number[],
    title: "",
    categoryIds: [] as number[],
    priority: 0,
    isActive: true,
    typeId: "",
  });

  const fetchModules = async () =>
    await Module.list({ state: "modules", params: { isActive: true } });

  const fetchZones = async (moduleId?: any) =>
    await Zone.list({
      state: "zones",
      params: { moduleId: moduleId || undefined },
    });

  const fetchCategories = async (moduleId?: any) =>
    await Category.list({
      state: "categories",
      params: { moduleId: moduleId || undefined, limit: 1000 },
    });
  const fetchStoreTypes = async (moduleId?: any) => {
    if (!moduleId) {
      return;
    }
    await StoreType.list({
      state: "storeTypes",
      params: { moduleId, limit: 400000 },
    });
  };

  useEffect(() => {
    fetchModules();
    if (initialValues.moduleId) {
      fetchZones(initialValues.moduleId);
      fetchCategories(initialValues.moduleId);
      fetchStoreTypes(initialValues.moduleId);
    }
  }, []);

  useEffect(() => {
    if (state && state.id && state.isSectionTitleEdit) {
      setInitialValues({
        moduleId: state.moduleId || "",
        zoneIds: state.zoneIds || [],
        title: state.title || "",
        categoryIds: state.categoryIds || [],
        priority: state.priority || 0,
        isActive: state.isActive !== false,
        typeId: state.typeId || state.type?.id || "",
      });
      if (state.moduleId) {
        fetchZones(state.moduleId);
        fetchCategories(state.moduleId);
        fetchStoreTypes(state.moduleId);
      }
    } else {
      setInitialValues({
        moduleId: "",
        zoneIds: [],
        title: "",
        categoryIds: [],
        priority: 0,
        isActive: true,
        typeId: "",
      });
    }
  }, [state]);

  const handleSubmit = async (
    values: typeof initialValues,
    { resetForm }: any,
  ) => {
    if (
      !values.moduleId ||
      !values.title ||
      values.zoneIds.length === 0 ||
      values.categoryIds.length === 0
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    const payload = {
      moduleId: values.moduleId,
      zoneIds: values.zoneIds,
      title: values.title,
      categoryIds: values.categoryIds,
      priority: Number(values.priority) || 0,
      isActive: values.isActive,
      typeId: values.typeId || undefined,
    };

    const isEdit = state && state.id && state.isSectionTitleEdit;

    const promise = isEdit
      ? CategorySections.update({
          state: "categorySections",
          id: state.id,
          data: payload,
        })
      : CategorySections.create({ state: "categorySections", data: payload });

    await toast.promise(promise, {
      pending: isEdit
        ? "Updating Section Title..."
        : "Creating Section Title...",
      success: isEdit ? "Section Title updated!" : "Section Title created!",
      error: "Error saving Section Title",
    });

    resetForm();
    navigate("/admin/product-management/categories", { replace: true });
    await CategorySections.list({ state: "categorySections", params: {} });
  };

  return (
    <Wrapper stylewrapper="border-radius:12px">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, resetForm, isSubmitting }: any) => (
          <Form>
            <Row>
              <FormSection style={{ width: "100%" }}>
                <Column>
                  <Row>
                    <ModuleDropdown
                      name="moduleId"
                      styleInput="width:95.5%"
                      onChange={(val) => {
                        setFieldValue("moduleId", val);
                        setFieldValue("zoneIds", []);
                        setFieldValue("categoryIds", []);
                        setFieldValue("typeId", "");
                        fetchZones(val);
                        fetchCategories(val);
                        fetchStoreTypes(val);
                      }}
                    />
                    <Fields.DropDown
                      name="zoneIds"
                      label="Zones"
                      placeholder="---Select Zones---"
                      options={zones}
                      titleAsKey="name"
                      valueAsKey="id"
                      styleInput="width:94.5%"
                      styleOptionsWrapper="width:100%"
                      isMultiple
                      selectable
                      disabled={!values.moduleId}
                    />
                  </Row>

                  <Row>
                    <Fields.Input
                      name="title"
                      placeholder="Enter Section Title (e.g. Daily Essentials)"
                      label="Section Title"
                    />
                    <Fields.Input
                      name="priority"
                      type="number"
                      placeholder="0"
                      label="Priority (Order)"
                    />
                  </Row>

                  <Row>
                    <div style={{ width: "100%" }}>
                      <Fields.DropDown
                        name="categoryIds"
                        label="Categories"
                        placeholder="---Select Categories---"
                        options={categories}
                        titleAsKey="name"
                        valueAsKey="id"
                        styleInput="width:94.5%"
                        styleOptionsWrapper="width:100%"
                        isMultiple
                        selectable
                        hasSearch
                        disabled={!values.moduleId}
                      />
                    </div>
                  </Row>

                  <Row>
                    <div style={{ width: "100%" }}>
                      <Fields.DropDown
                        name="typeId"
                        label="Store Type"
                        placeholder="---Select Store Type---"
                        options={storeTypes}
                        titleAsKey="name"
                        valueAsKey="id"
                        styleInput="width:94.5%"
                        styleOptionsWrapper="width:100%"
                        disabled={!values.moduleId}
                      />
                    </div>
                  </Row>
                  <Row>
                    <Fields.Switch name="isActive" label="Active" />
                  </Row>
                </Column>
              </FormSection>
            </Row>

            <br />
            <ButtonWrapper>
              <Button
                title="Reset"
                type="reset"
                variant="reset"
                onClick={() => resetForm()}
                disabled={isSubmitting}
              />
              <Button
                title={
                  state?.id && state?.isSectionTitleEdit ? "Update" : "Save"
                }
                type="submit"
                disabled={isSubmitting}
              />
            </ButtonWrapper>
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
};

export default SectionTitleForm;
